import '../css/SubscribeForm.less';

import React, {
  useState,
  FunctionComponent,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import {CardElement, useElements, useStripe} from '@stripe/react-stripe-js';
import {
  Button,
  Dropdown,
  DropdownProps,
  Input,
  Message,
  StrictInputProps,
  Checkbox,
  Popup,
} from 'semantic-ui-react';

import {
  useCreateCustomerSubscriptionMutation,
  CreateCustomerSubscriptionMutation,
  Plan,
  Maybe,
  CreateStorageSubscriptionMutation,
  useCreateStorageSubscriptionMutation,
} from '../generated/graphql';
import SubscriptionAgreement from './SubscriptionAgreement';
import {
  propagateErrorsContext,
  extractErrorMessageFromApolloError,
  doNotRetryContext,
} from '../util/errors';
import {slugFormat} from '../util/text';
import {captureError} from '../util/integrations';
import _ from 'lodash';
import {ExecutionResult} from 'react-apollo';
import makeComp from '../util/profiler';
import {PlanTable} from '../pages/BillingPage';
import * as S from './SubscribeForm.styles';

export interface PricingPlan {
  id: string;
  name: string;
  unitPrice: number;
  maxSeats: number;
  billingInterval: string;
}

export interface SubscribeFormProps {
  custoEmail: string;
  monthlyPlan: PricingPlan;
  yearlyPlan: PricingPlan;
  storageMonthly: Maybe<Plan>;
  trackingMonthly: Maybe<Plan>;
  pricingPlans: PlanTable;
  onSubscriptionCompleted(success: boolean): void;
}

export const SubscribeForm: FunctionComponent<SubscribeFormProps> = makeComp(
  props => {
    const {
      monthlyPlan,
      yearlyPlan,
      storageMonthly,
      trackingMonthly,
      pricingPlans,
    } = props;

    const [custoName, setCustoName] = useState('');
    const [organizationName, setOrganizationName] = useState('');
    const [agreementChecked, setAgreementChecked] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(props.yearlyPlan);
    const [units, setUnits] = useState(props.yearlyPlan.maxSeats);
    const [error, setError] = useState<string | null>(null);
    const [formDisabled, setFormDisabled] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const {onSubscriptionCompleted} = props;

    const stripe = useStripe();
    const elements = useElements();
    useEffect(() => {
      if (!stripe || !elements) {
        setFormDisabled(true);
      } else {
        setFormDisabled(false);
      }
    }, [elements, stripe, setFormDisabled]);

    const [
      createCustomerSubscriptionMutation,
    ] = useCreateCustomerSubscriptionMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

    const [
      createStorageSubscriptionMutation,
    ] = useCreateStorageSubscriptionMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

    const onUnitsChanged = useCallback<NonNullable<DropdownProps['onChange']>>(
      (event, data) => {
        const newUnits = data.value as number;
        setUnits(newUnits);
      },
      [setUnits]
    );
    const onNameChanged = useCallback<
      NonNullable<StrictInputProps['onChange']>
    >(
      (event, data) => {
        setCustoName(data.value);
      },
      [setCustoName]
    );
    const onOrganizationNameChanged = useCallback<
      NonNullable<StrictInputProps['onChange']>
    >(
      (event, data) => {
        setOrganizationName(slugFormat(data.value));
      },
      [setOrganizationName]
    );

    const {custoEmail} = props;
    const storageID = storageMonthly?.id;
    const trackingID = trackingMonthly?.id;

    const handleSubscribe = useCallback(
      async (trial: boolean) => {
        if (!stripe || !elements) {
          throw new Error('Stripe not loaded.');
        }

        if (organizationName == null || organizationName.length === 0) {
          setError('Must provide organization name.');
          return false;
        }
        if (!agreementChecked) {
          setError('You must agree to the terms before subscribing.');
          return false;
        }

        if (storageID == null || trackingID == null) {
          return false;
        }

        const cardElement = elements.getElement(CardElement);
        if (cardElement == null) {
          throw new Error('Stripe card element not loaded.');
        }

        window.analytics.track('Subscribe purchase button clicked', {
          email: custoEmail,
          plan: selectedPlan.id,
          units,
          trial,
        });

        const paymentMethod = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: custoName,
            email: custoEmail,
          },
        });
        if (
          paymentMethod.error != null ||
          paymentMethod.paymentMethod == null
        ) {
          console.log(paymentMethod.error);
          setError(
            paymentMethod.error?.message ?? 'Error validating payment method.'
          );
          return false;
        }

        let createSubResult: ExecutionResult<CreateCustomerSubscriptionMutation>;

        const onError = (opts: {
          err: any;
          op: any;
          extra: {[key: string]: any};
          analyticsEvent: any;
          userFacingErrorMsg: any;
        }) => {
          const variables = {
            email: custoEmail,
            units,
            trial,
            ...opts.extra,
          };
          captureError(opts.err, `SubscribeForm-${opts.op}`, {
            extra: {
              variables,
            },
          });
          if (opts.analyticsEvent != null) {
            window.analytics.track(opts.analyticsEvent, {
              variables,
            });
          }
          setError(opts.userFacingErrorMsg);
          onSubscriptionCompleted(/*success=*/ false);
        };

        try {
          createSubResult = await createCustomerSubscriptionMutation({
            variables: {
              newOrganizationName: organizationName,
              planId: selectedPlan.id,
              paymentMethod: paymentMethod.paymentMethod.id,
              trial,
              quantity: units,
            },
          });
        } catch (err) {
          captureError(err, 'SubscribeForm-createSubResult', {
            extra: {email: custoEmail, plan: selectedPlan.id, units},
          });
          const errMsg = extractErrorMessageFromApolloError(err) ?? err.message;
          const userFacingErrMsg = trial
            ? `Error processing trial: ${errMsg}`
            : `Error processing payment: ${errMsg}`;
          onError({
            err,
            op: 'createSubResult',
            extra: {
              plan: selectedPlan.id,
              reason: errMsg,
              trial,
              units,
            },
            analyticsEvent: 'SubscribeForm-createSubResult',
            userFacingErrorMsg: userFacingErrMsg,
          });
          return false;
        }

        if (
          createSubResult.errors == null &&
          createSubResult.data &&
          createSubResult.data.createCustomerSubscription &&
          createSubResult.data.createCustomerSubscription.subscription
        ) {
          const {
            latest_invoice,
          } = createSubResult.data.createCustomerSubscription.subscription;
          const {payment_intent} = latest_invoice;
          if (payment_intent) {
            const {client_secret, status} = payment_intent;
            if (
              status === 'requires_action' ||
              status === 'requires_payment_method'
            ) {
              try {
                const result = await stripe.confirmCardPayment(client_secret);
                if (result.error) {
                  onError({
                    err: result.error.message,
                    op: 'handleCardPayment',
                    extra: {
                      email: custoEmail,
                      plan: selectedPlan.id,
                      units,
                      trial,
                    },
                    analyticsEvent: null,
                    userFacingErrorMsg: `We created your organization, but had trouble confirming your payment: ${result.error.message}`,
                  });

                  return false;
                }
              } catch (error) {
                onError({
                  err: error,
                  op: 'handleCardPayment',
                  extra: {
                    email: custoEmail,
                    plan: selectedPlan.id,
                    units,
                    trial,
                  },
                  analyticsEvent: null,
                  userFacingErrorMsg: `We created your organization, but had trouble confirming your payment: ${error}`,
                });

                return false;
              }
            }
          }
        } else {
          onError({
            err:
              "Unknown error confirming subscription. Exception didn't throw but success was not reported.",
            op: 'handleSubscribe-unknown',
            extra: {email: custoEmail, plan: selectedPlan.id, units, trial},
            analyticsEvent: null,
            userFacingErrorMsg:
              'Error confirming payment. Please wait a few moments or contact support@wandb.com for help.',
          });
          return false;
        }

        /* Sign up for storage */
        let createStorageSubResult: ExecutionResult<CreateStorageSubscriptionMutation>;
        try {
          createStorageSubResult = await createStorageSubscriptionMutation({
            variables: {
              storagePlanId: storageID,
              trackingPlanId: trackingID,
              paymentMethod: paymentMethod.paymentMethod.id,
              organizationId:
                createSubResult.data.createCustomerSubscription.organizationId,
              trial,
            },
          });
        } catch (err) {
          const errMsg = extractErrorMessageFromApolloError(err) ?? err.message;
          onError({
            err,
            op: 'createStorageSubResult',
            extra: {
              storageID,
              trackingID,
              reason: errMsg,
            },
            analyticsEvent: 'Error processing subscription payment',
            userFacingErrorMsg: `Error processing storage subscription payment: ${errMsg}`,
          });
          return false;
        }

        if (
          createStorageSubResult.errors != null ||
          !createStorageSubResult.data?.createStorageSubscription?.subscription
        ) {
          onError({
            err:
              "Unknown error confirming subscription. Exception didn't throw but success was not reported.",
            op: 'SubscribeForm-handleStorageSubscribe-unknown',
            extra: {email: custoEmail, storageID, trackingID, units, trial},
            analyticsEvent: null,
            userFacingErrorMsg:
              'Error confirming payment. Please wait a few moments or contact support@wandb.com for help.',
          });
          return false;
        }

        const {
          payment_intent_storage,
        } = createStorageSubResult.data.createStorageSubscription.subscription;

        if (payment_intent_storage) {
          const {client_secret, status} = payment_intent_storage;
          if (
            status === 'requires_action' ||
            status === 'requires_payment_method'
          ) {
            try {
              const result = await stripe.confirmCardPayment(client_secret);
              if (result.error) {
                captureError(
                  result.error.message!,
                  'SubscribeForm-handleCardPaymentStorage',
                  {
                    extra: {
                      email: custoEmail,
                      storageID,
                      trackingID,
                      units,
                      trial,
                    },
                  }
                );
                setError(
                  `We had trouble confirming your payment: ${result.error.message}`
                );
                onSubscriptionCompleted(/*success=*/ false);
                return false;
              }
            } catch (error) {
              captureError(error, 'SubscribeForm-handleCardPaymentStorage', {
                extra: {
                  email: custoEmail,
                  storageID,
                  trackingID,
                  units,
                  trial,
                },
              });
              setError(`We had trouble confirming your payment: ${error}`);
              onSubscriptionCompleted(/*success=*/ false);
              return false;
            }
          }
        }
        onSubscriptionCompleted(/*success=*/ true);
        return true;
      },
      [
        stripe,
        elements,
        organizationName,
        agreementChecked,
        custoEmail,
        selectedPlan.id,
        units,
        custoName,
        createCustomerSubscriptionMutation,
        onSubscriptionCompleted,
        createStorageSubscriptionMutation,
        storageID,
        trackingID,
      ]
    );

    const onSubscribe = useCallback(
      async (trial: boolean) => {
        // Clear status messages and disable further input.
        setError(null);
        setFormDisabled(true);

        const completedSuccessfully = await handleSubscribe(trial);

        // Make form editable again after results are in.
        if (!completedSuccessfully) {
          setFormDisabled(false);
        }
      },
      [handleSubscribe, setError, setFormDisabled]
    );

    const trialPeriodEnd = useMemo(() => {
      const trialEnd = new Date();
      trialEnd.setDate(trialEnd.getDate() + 30);
      return trialEnd.toLocaleDateString();
    }, []);

    const possibleUnits = useMemo(
      () =>
        _.range(1, selectedPlan.maxSeats + 1).map(i => ({
          key: i,
          text: i,
          value: i,
        })),
      [selectedPlan.maxSeats]
    );

    const yearlySavingsPct = useMemo(
      () =>
        Math.floor(
          ((monthlyPlan.unitPrice - yearlyPlan.unitPrice / 12) /
            monthlyPlan.unitPrice) *
            100
        ),
      [monthlyPlan.unitPrice, yearlyPlan.unitPrice]
    );

    const pricePerPeriod = useMemo(
      () => ((selectedPlan.unitPrice * units) / 100).toLocaleString(),
      [selectedPlan.unitPrice, units]
    );

    return (
      <div className="checkout-form">
        {error != null && (
          <Message negative content={error} className="alert" />
        )}
        <div className="billing-period-picker">
          <Button
            data-test="monthly-button"
            active={selectedPlan.id === monthlyPlan.id}
            onClick={() => setSelectedPlan(monthlyPlan)}
            disabled={formDisabled}>
            Monthly
          </Button>
          <Popup
            content={`Save ${yearlySavingsPct}%`}
            trigger={
              <Button
                data-test="yearly-button"
                active={selectedPlan.id === yearlyPlan.id}
                onClick={() => setSelectedPlan(yearlyPlan)}
                disabled={formDisabled}>
                Yearly
              </Button>
            }></Popup>
        </div>
        <div className="unit-picker">
          {`$${selectedPlan.unitPrice / 100} x `}
          <Dropdown
            data-test="choose-seats-dropdown"
            selection
            options={possibleUnits}
            defaultValue={selectedPlan.maxSeats}
            className="seat-field"
            onChange={onUnitsChanged}
            disabled={formDisabled}
          />
          {` seats = $${pricePerPeriod} per ${selectedPlan.billingInterval}`}
        </div>
        <S.PricingTable>
          <S.PricingTableRow>
            <S.PricingTableHeader>
              <Popup
                content={
                  <React.Fragment>
                    <div style={{color: '#fff'}}>
                      Files hosted on W&B servers, including logged media, model
                      checkpoints, and uploaded datasets
                    </div>
                  </React.Fragment>
                }
                trigger={<div>{pricingPlans.storageFeatureTitle}</div>}
                position="top left"
                inverted
                size="mini"
              />
            </S.PricingTableHeader>
            <S.PricingTableHeader>
              <Popup
                content={
                  <React.Fragment>
                    <div style={{color: '#fff'}}>
                      Files tracked by Artifacts, including files stored on
                      external servers such as Google Cloud or an AWS S3 bucket
                    </div>
                  </React.Fragment>
                }
                trigger={<div>{pricingPlans.artifactFeatureTitle}</div>}
                position="top left"
                inverted
                size="mini"
              />
            </S.PricingTableHeader>
          </S.PricingTableRow>
          <S.PricingTableRow>
            <S.PricingTableBody>
              {pricingPlans.storageFeatures.map(tf => (
                <S.PricingTableFeatureRow>{tf}</S.PricingTableFeatureRow>
              ))}
            </S.PricingTableBody>
            <S.PricingTableBody>
              {pricingPlans.artifactFeatures.map(tf => (
                <S.PricingTableFeatureRow>{tf}</S.PricingTableFeatureRow>
              ))}
            </S.PricingTableBody>
          </S.PricingTableRow>
          <S.PricingTableFooter>
            Storage and artifact usage is billed monthly
          </S.PricingTableFooter>
        </S.PricingTable>
        <div className="checkout-fields">
          <Input
            className="custo-input"
            placeholder="Email address"
            defaultValue={custoEmail}
            disabled={true}
          />
          <Input
            data-test="org-name-input"
            className="custo-input"
            placeholder="Organization name"
            value={organizationName}
            onChange={onOrganizationNameChanged}
            disabled={formDisabled}
          />
          <Input
            data-test="cardholder-name-input"
            className="custo-input"
            placeholder="Name on card"
            onChange={onNameChanged}
            disabled={formDisabled}
          />
          <div className="card-element">
            <CardElement
              options={{
                disabled: formDisabled,
                style: {
                  base: {
                    fontSize: '15px',
                    color: '#424770',
                    fontFamily: 'sans-serif',
                    fontWeight: '300',
                    letterSpacing: '0.025em',
                    '::placeholder': {
                      color: 'rgba(55, 59, 62, 0.45)',
                    },
                  },
                },
              }}
            />
          </div>
          <Checkbox
            onChange={(_1, data) => setAgreementChecked(data.checked ?? false)}
            label={
              <label>
                I agree to the{' '}
                <span
                  className="link-span"
                  onClick={e => {
                    setTermsModalOpen(true);
                    e.stopPropagation();
                  }}>
                  subscription agreement.
                </span>
              </label>
            }
            disabled={formDisabled}
          />
          <div className="subscribe-button-group">
            <Button
              className="subscribe-button"
              color="blue"
              onClick={() => {
                if (!showConfirmation) {
                  setShowConfirmation(true);
                } else {
                  onSubscribe(/*trial=*/ true);
                }
              }}
              disabled={formDisabled}>
              {formDisabled
                ? 'Processing...'
                : showConfirmation
                ? 'Confirm'
                : 'Try free for 30 days'}
            </Button>
            {showConfirmation ? (
              <div className="checkout-fineprint">
                {`By clicking confirm, you authorize Weights & Biases to charge
              you $${pricePerPeriod} when your trial ends on ${trialPeriodEnd},
              and then again on the same day every ${selectedPlan.billingInterval}.
              If you do not wish to be charged, you may return to this page at any
              time and cancel your subscription. Weights & Biases will email you
              trial expiration reminders 7 and 3 days before your trial ends.`}
              </div>
            ) : (
              <>
                <div>or</div>
                <Button
                  className="subscribe-button"
                  onClick={() => onSubscribe(/*trial=*/ false)}
                  disabled={formDisabled}>
                  {formDisabled ? 'Processing...' : 'Subscribe now'}
                </Button>{' '}
              </>
            )}
          </div>
        </div>
        <SubscriptionAgreement
          open={termsModalOpen}
          onClose={() => setTermsModalOpen(false)}
        />
      </div>
    );
  },
  {id: 'SubscribeForm'}
);
